import * as THREE from 'three';
import { FLOW_CIRCUIT as circuit } from '../circuits.js';
import { planBattles } from '../flow-stories.js';
import { sampleRace } from '../race-core.js';

const schedules = new WeakMap();
const vector = point => new THREE.Vector3(point.x, point.y, point.z);

function schedule(round) {
  if (schedules.has(round)) return schedules.get(round);
  const { actions, story } = planBattles(round), cuts = [0];
  const threeWay = round.raceVersion >= 6 ? story.threeWay : round.scenarioIndex === 7;
  for (const nominal of [4, 8, 11, 15, 19, 23, 27, 31, 34]) {
    const active = actions.find(action => nominal > action.start + .02 && nominal < action.end);
    const time = active ? active.end : nominal;
    if (time - cuts.at(-1) >= 2.5 && time <= 34.5) cuts.push(time);
  }
  cuts.push(37, 40);
  const cornerBattle = round.raceVersion >= 6 && actions.find(action => ['switchback', 's-duel'].includes(action.tactic));
  // Keep the crossover in one shot, then follow the same pair through the exit.
  const exitCut = cornerBattle ? cornerBattle.start + (cornerBattle.end - cornerBattle.start) * .59 : null;
  if (exitCut !== null) { cuts.push(exitCut); cuts.sort((a, b) => a - b); }
  const shots = cuts.slice(0, -1).map((start, i) => {
    const end = cuts[i + 1], middle = (start + end) / 2;
    const mode = exitCut !== null && start >= exitCut && start < cornerBattle.end ? 'corner-exit'
      : start >= 37 ? 'photo' : middle < 4 ? 'rear-battle' : middle < 10 ? 'pack'
      : middle < 19 ? 's-overview' : middle < 24.5 ? (round.scenarioIndex % 3 === 1 ? 'hood' : 'curb')
        : middle < 30 ? 'pan' : 'rear-battle';
    const terminal = sampleRace(round, end), opening = sampleRace(round, start);
    const battle = actions.find(action => action.start < end - .05 && action.end > start + .05);
    const subjectIds = mode === 'pack' ? [...opening.order]
      : mode === 'photo' ? round.finalOrder.slice(0, threeWay ? 3 : 2)
      : battle ? [battle.attacker, battle.defender] : opening.order.slice(0, 2);
    return { subjectIds, onboardId: opening.order[1], id: `${round.id}:${round.gridSeed}:${i}`, start, end, mode,
      station: Math.max(...terminal.cars.map(car => car.distance)) + (mode === 'curb' ? 32 : 80) };
  });
  schedules.set(round, shots);
  return shots;
}

function point(distance, lane, height) { return { ...circuit.pose(distance, lane), y: height }; }

function fittedFov(position, target, subjects, aspect, minimum) {
  const eye = vector(position), forward = vector(target).sub(eye).normalize();
  const right = new THREE.Vector3().crossVectors(forward, new THREE.Vector3(0, 1, 0)).normalize();
  const up = new THREE.Vector3().crossVectors(right, forward);
  let tangent = Math.tan(THREE.MathUtils.degToRad(minimum / 2));
  for (const car of subjects) for (const side of [-1.3, 1.3]) for (const longitudinal of [-2.7, 2.7]) for (const y of [.02, 1.65]) {
    const p = new THREE.Vector3(car.pose.x + side * Math.cos(car.pose.heading) + longitudinal * Math.sin(car.pose.heading), y,
      car.pose.z - side * Math.sin(car.pose.heading) + longitudinal * Math.cos(car.pose.heading)).sub(eye);
    const depth = Math.max(1, p.dot(forward));
    tangent = Math.max(tangent, Math.abs(p.dot(right)) / (depth * aspect * .82), Math.abs(p.dot(up)) / (depth * .68));
  }
  return THREE.MathUtils.radToDeg(2 * Math.atan(tangent));
}

export function planBroadcastShot(round, sample, aspect) {
  const shot = schedule(round).find(shot => sample.time < shot.end) || schedule(round).at(-1);
  const leader = sample.cars.find(car => car.id === sample.leader);
  const subjects = shot.subjectIds.map(id => sample.cars.find(car => car.id === id));
  const center = subjects.reduce((sum, car) => ({ x: sum.x + car.pose.x / subjects.length, y: .68, z: sum.z + car.pose.z / subjects.length }), { x: 0, y: .68, z: 0 });
  const distance = subjects.reduce((sum, car) => sum + car.distance / subjects.length, 0);
  let position, target = center, minimum = 45, fixed = false;
  switch (shot.mode) {
    case 'pack': position = point(shot.station, -8.9, 2.3); minimum = 4.5; fixed = true; break;
    case 'curb': position = point(shot.station, 8.9, 1.25); minimum = 13; fixed = true; break;
    case 's-overview': position = point((circuit.curveStart + circuit.curveEnd) / 2 + 60, -25, 22); minimum = 10; fixed = true; break;
    case 'corner-exit': position = point(distance + 8, -14.5, 11); minimum = 52; break;
    case 'pan': position = point(distance + 3, 14.5, 9); minimum = 56; break;
    case 'hood': {
      const car = sample.cars.find(car => car.id === shot.onboardId);
      position = { x: car.pose.x + Math.sin(car.pose.heading) * 1.55, y: 1.12, z: car.pose.z + Math.cos(car.pose.heading) * 1.55 };
      target = point(car.distance + 34, circuit.racingLine(car.distance + 34, round.raceVersion), .65);
      return { ...shot, position, target, fov: 69, subjects, fixed: false };
    }
    case 'photo': position = point(leader.distance + 27, -8.8, 3.1); minimum = 46; break;
    default: position = point(distance - 17, -8.7, 3.15); minimum = 53;
  }
  const fov = fittedFov(position, target, subjects, aspect, minimum);
  return { ...shot, position, target, fov, subjects, fixed };
}
